import axios from 'axios';
import Cookie from 'js-cookie';
/**
 * The same cookie the datahub client reads, and deliberately so.
 *
 * booqly's Azure sign-in already returns an Entra id_token issued by the very app registration the
 * Efkt fe-gateway validates against — same `aud`, same `iss`, and `localStorage['auth.schema']` is
 * already `Bearer`. Verified against stage: no token gets 401 from `POST /api/order`, this cookie
 * gets 400 `id is required`, i.e. it authenticates and reaches endpoint validation.
 *
 * So `/v2` needs no second identity provider, no MSAL, and no login flow of its own — and token
 * refresh keeps working through the iframe `Page.tsx` already renders.
 */
const TOKEN_KEY = 'auth.token';
const PREFERRED_LOCALE_KEY = 'locale';
let errorHandler;
export const endpoints = {
    // `||` and not `??`: every key in `.env.example` is empty, and `dotenv-webpack` runs with
    // `safe: false`, so an unset var arrives as `''` rather than `undefined`. `??` would let the
    // empty string through and axios would resolve every path against the app's own origin.
    efkt: process.env.NEXT_PUBLIC_EFKT_API_ENDPOINT || '',
};
function getAuthToken() {
    return typeof window === 'undefined' ? undefined : Cookie.get(TOKEN_KEY);
}
function getAcceptLanguage() {
    if (typeof window === 'undefined')
        return undefined;
    // Read the raw key rather than importing `@libs/localization`, whose barrel calls `i18n.init()`
    // at import time — a side effect the API layer has no business triggering.
    const locale = window.localStorage.getItem(PREFERRED_LOCALE_KEY);
    return locale ? `${locale};q=1.0, da;q=0.9, en;q=0.8` : undefined;
}
function createClient(baseURL) {
    const client = axios.create({
        baseURL,
    });
    client.interceptors.request.use((requestConfig) => {
        const token = getAuthToken();
        if (token) {
            requestConfig.headers.Authorization = `Bearer ${token}`;
        }
        const acceptLanguage = getAcceptLanguage();
        if (acceptLanguage) {
            requestConfig.headers['Accept-Language'] = acceptLanguage;
        }
        return requestConfig;
    });
    client.interceptors.response.use(undefined, async (error) => {
        var _a;
        const requestConfig = error.config;
        // One retry on 401, in case the refresh iframe replaced the cookie mid-flight. It has to live
        // here rather than in react-query, because `query.client.ts` sets `retry: false` globally.
        // Bounded to a single attempt so an genuinely expired session fails instead of looping.
        if (((_a = error.response) === null || _a === void 0 ? void 0 : _a.status) === 401 && requestConfig && !requestConfig._retriedAfter401) {
            requestConfig._retriedAfter401 = true;
            return client.request(requestConfig);
        }
        if (errorHandler) {
            errorHandler(error);
        }
        // Unlike the datahub client, this one RE-REJECTS. That client resolves with `undefined` on
        // failure, which is why every datahub method is written `response?.data`. Here react-query
        // must see the rejection: the order flow maps 409/422/502 to different user prompts, and a
        // swallowed error would render as an empty screen instead.
        return Promise.reject(error);
    });
    return client;
}
function setErrorHandler(handler) {
    errorHandler = handler;
}
export default {
    createClient,
    getAuthToken,
    setErrorHandler,
};
